/**
파일명: useAppRouter.js
설명: 브라우저 경로를 앱 라우트 상태로 변환하고 내비게이션 동작을 제공하는 라우팅 훅입니다.
*/

import { useEffect, useMemo, useState } from 'react';
import { getRoute, normalizePath } from '../utils/routing';

// 페이지 이동할 때 스크롤을 맨 위로 올린다. 단, 동일 섹션 내 탭 이동 시에는 스크롤 위치를 유지한다.
function safeScrollToTop() {
  // 브라우저 환경에서만 존재한다.
  if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
    window.scrollTo(0, 0);
  }
}

export function useAppRouter() {
  const [pathname, setPathname] = useState(() => (typeof window !== 'undefined' ? normalizePath(window.location.pathname) : '/'));

  // 뒤로가기/앞으로가기 감시 및 스크롤 위치 관리
  // 컴포넌트가 화면에 처음 나타났을 때 딱 한 번 실행  (끝에 [] 가 있으면 한 번만 실행)
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handlePopState = () => {
      setPathname(normalizePath(window.location.pathname));
      safeScrollToTop();
    };

    safeScrollToTop();
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);


  // 메뉴 클릭 시 이동할 때 URL을 변경하고 pathname 상태를 업데이트
  // [기능] href로 이동합니다. options.replace가 true면 히스토리에 항목을 남기지 않습니다.
  // [비즈니스] 구 URL을 새 URL로 자동 전환할 때는 replace를 써야 합니다.
  //            pushState로 바꾸면 사용자가 뒤로가기를 눌렀을 때 구 URL로 돌아가
  //            다시 전환되는 무한 루프에 빠집니다.
  const navigate = (href, options = {}) => {
    const nextPath = normalizePath(href);

    if (typeof window !== 'undefined' && window.location.pathname !== nextPath) {
      // URL 변경 (history.pushState는 새로고침 없이 주소창 URL만 바꾸는 브라우저 기능)
      if (options.replace) {
        window.history.replaceState({}, '', nextPath);
      } else {
        window.history.pushState({}, '', nextPath);
      }
    }

    setPathname(nextPath);  // React 상태 갱신 → 화면 다시 그림

    const currentRoot = pathname.split('/')[1];
    const nextRoot = nextPath.split('/')[1];
    
    // 동일 섹션(company, solution 등) 내 이동 시 스크롤 유지, 섹션 간 이동 시 스크롤 맨 위로
    if (currentRoot !== nextRoot) {
      safeScrollToTop();
    }
  };

  // pathname이 바뀔 때만 다시 계산하도록 useMemo로 최적화
  const route = useMemo(() => getRoute(pathname), [pathname]);

  return { pathname, route, navigate };
}
